import type {AdminLanguage} from "../../utils/language"

export const CONTENTFUL_NOTIFICATIONS_LIMIT = 50
export const NOTIFICATIONS_REFRESH_INTERVAL_MS = 30 * 60 * 1000
export const NOTIFICATIONS_READ_STORAGE_PREFIX = "admin_notifications_read_v1"

export interface ContentfulNotificationsConfig {
    spaceId: string
    environment: string
    deliveryToken: string
    contentType: string
}

export interface AdminNotificationLocalizedText {
    ru: string
    uz: string
}

export interface AdminNotification {
    id: string
    title: AdminNotificationLocalizedText
    summary: AdminNotificationLocalizedText
    details: AdminNotificationLocalizedText
    imageUrl?: string
    publishedAt: string
}

interface ContentfulSysLink {
    id?: unknown
}

interface ContentfulLinkValue {
    sys?: ContentfulSysLink
}

interface ContentfulEntry {
    sys?: {
        id?: unknown
        publishedAt?: unknown
        updatedAt?: unknown
        createdAt?: unknown
    }
    fields?: Record<string, unknown>
}

interface ContentfulAsset {
    sys?: ContentfulSysLink
    fields?: Record<string, unknown>
}

interface ContentfulResponse {
    items?: unknown
    includes?: {
        Asset?: unknown
    }
}

const isRecord = (value: unknown): value is Record<string, unknown> => (
    typeof value === "object" && value !== null && !Array.isArray(value)
)

const normalizeTextValue = (value: unknown) => typeof value === "string" ? value.trim() : ""

const getLocalizedValue = (value: unknown, locale: string, allowAnyFallback = true): unknown => {
    if (!isRecord(value)) return value
    if ("sys" in value || "url" in value) return value
    const localeCandidates = locale.toLowerCase().startsWith("uz")
        ? [locale, "uz-UZ", "uz"]
        : [locale, "ru-RU", "ru"]
    for (const candidate of localeCandidates) {
        if (candidate in value) return value[candidate]
    }
    if (!allowAnyFallback) return undefined
    if ("ru-RU" in value) return value["ru-RU"]
    if ("ru" in value) return value.ru
    return Object.values(value).find((item) => item !== undefined && item !== null)
}

const normalizeLocalizedText = (value: unknown): AdminNotificationLocalizedText => {
    const ru = normalizeTextValue(getLocalizedValue(value, "ru-RU", false))
    const uz = normalizeTextValue(getLocalizedValue(value, "uz-UZ", false)) || ru
    return {ru, uz}
}

const getLinkId = (value: unknown) => {
    const localizedValue = getLocalizedValue(value, "ru-RU")
    if (!isRecord(localizedValue)) return undefined
    const link = localizedValue as ContentfulLinkValue
    return typeof link.sys?.id === "string" ? link.sys.id : undefined
}

const normalizeAssetUrl = (value: unknown) => {
    const url = normalizeTextValue(value)
    if (!url) return undefined
    if (url.startsWith("//")) return `https:${url}`
    return url
}

export const getNotificationText = (
    value: AdminNotificationLocalizedText,
    language: AdminLanguage
) => value[language] || value.ru

export const normalizeContentfulNotifications = (response: unknown): AdminNotification[] => {
    if (!isRecord(response)) return []
    const contentfulResponse = response as ContentfulResponse
    const includedAssets = contentfulResponse.includes?.Asset
    const entries: unknown[] = Array.isArray(contentfulResponse.items) ? contentfulResponse.items : []
    const assets: unknown[] = Array.isArray(includedAssets) ? includedAssets : []
    const assetMap = new Map<string, ContentfulAsset>()

    assets.forEach((asset) => {
        if (!isRecord(asset)) return
        const typedAsset = asset as ContentfulAsset
        if (typeof typedAsset.sys?.id === "string") assetMap.set(typedAsset.sys.id, typedAsset)
    })

    return entries.flatMap((entry): AdminNotification[] => {
        if (!isRecord(entry)) return []
        const typedEntry = entry as ContentfulEntry
        const id = typeof typedEntry.sys?.id === "string" ? typedEntry.sys.id : ""
        const publishedAt = [
            typedEntry.sys?.publishedAt,
            typedEntry.sys?.updatedAt,
            typedEntry.sys?.createdAt
        ].find((value): value is string => typeof value === "string" && value.length > 0) || ""
        const fields = typedEntry.fields || {}
        const title = normalizeLocalizedText(fields.title)
        if (!id || !publishedAt || !title.ru) return []

        const imageAsset = assetMap.get(getLinkId(fields.image) || "")
        const imageFile = getLocalizedValue(imageAsset?.fields?.file, "ru-RU")
        const imageUrl = normalizeAssetUrl(isRecord(imageFile) ? imageFile.url : undefined)

        return [{
            id,
            title,
            summary: normalizeLocalizedText(fields.summary ?? fields.description),
            details: normalizeLocalizedText(fields.details ?? fields.description),
            imageUrl,
            publishedAt
        }]
    }).sort((left, right) => Date.parse(right.publishedAt) - Date.parse(left.publishedAt))
}

export const buildContentfulNotificationsUrl = (config: ContentfulNotificationsConfig) => {
    const params = new URLSearchParams({
        content_type: config.contentType,
        locale: "*",
        include: "1",
        limit: String(CONTENTFUL_NOTIFICATIONS_LIMIT),
        order: "-sys.updatedAt"
    })
    return `https://cdn.contentful.com/spaces/${encodeURIComponent(config.spaceId)}/environments/${encodeURIComponent(config.environment)}/entries?${params.toString()}`
}

export const fetchContentfulNotifications = async (
    config: ContentfulNotificationsConfig,
    signal?: AbortSignal
): Promise<AdminNotification[]> => {
    const response = await fetch(buildContentfulNotificationsUrl(config), {
        headers: {Authorization: `Bearer ${config.deliveryToken}`},
        signal
    })

    if (!response.ok) throw new Error(`Contentful request failed: ${response.status}`)
    return normalizeContentfulNotifications(await response.json())
}

export const getNotificationReadStorageKey = (accountId: number) => (
    `${NOTIFICATIONS_READ_STORAGE_PREFIX}:${accountId}`
)

export const parseReadNotificationIds = (value: string | null): Set<string> => {
    if (!value) return new Set()
    try {
        const parsed = JSON.parse(value)
        if (!Array.isArray(parsed)) return new Set()
        return new Set(parsed.filter((item): item is string => typeof item === "string" && item.length > 0))
    } catch {
        return new Set()
    }
}

export const readNotificationIds = (accountId: number) => {
    if (typeof window === "undefined") return new Set<string>()
    return parseReadNotificationIds(window.localStorage.getItem(getNotificationReadStorageKey(accountId)))
}

export const saveReadNotificationIds = (accountId: number, ids: Iterable<string>) => {
    if (typeof window === "undefined") return
    window.localStorage.setItem(getNotificationReadStorageKey(accountId), JSON.stringify(Array.from(new Set(ids))))
}

export const getContentfulNotificationsConfig = (): ContentfulNotificationsConfig | null => {
    const spaceId = process.env.REACT_APP_CONTENTFUL_SPACE_ID?.trim() || ""
    const environment = process.env.REACT_APP_CONTENTFUL_ENVIRONMENT?.trim() || "master"
    const deliveryToken = process.env.REACT_APP_CONTENTFUL_DELIVERY_TOKEN?.trim() || ""
    const contentType = process.env.REACT_APP_CONTENTFUL_CONTENT_TYPE?.trim() || "adminNotification"
    return spaceId && deliveryToken ? {spaceId, environment, deliveryToken, contentType} : null
}

export const CONTENTFUL_NOTIFICATIONS_CONFIG = getContentfulNotificationsConfig()
